/**
 * Toasts (owner, 2026-10-31: «convert all success messages and errors and validations into toast messages — sometimes
 * we cannot see those errors, we have to scroll to see»).
 *
 * One small store for the whole panel: every result line the views say through `useMsg()` — «Lagret.», a refusal from
 * the server, a check that failed before sending — appears in one stack fixed to the corner of the window
 * (components/Toaster.tsx), so it is seen wherever the page is scrolled. A field's own error stays red under the field
 * as well (the field to fix is the one that says so) and is also raised here.
 *
 * Rules:
 *  - `source`: one per message line (one per `useMsg()`). A new message from the same source REPLACES the old one, so
 *    «Lagrer …» becomes «Lagret.» in place instead of two toasts.
 *  - How long: «ok» 5 s, plain 6 s, «warn» 9 s, «err» 12 s; a line ending in «…» (work in progress) stays until it is
 *    replaced (at most 60 s). Hovering or focusing a toast holds it (WCAG 2.2.1); every toast has a close button.
 *  - At most five at once; the oldest goes first.
 * Pure TypeScript with no React, so a test can drive it.
 */
export type ToastTone = 'ok' | 'err' | 'warn' | '';
export interface Toast { id: number; text: string; tone: ToastTone; source?: string; ms: number; at: number }

const MAX = 5;
let seq = 0;
let list: Toast[] = [];
const listeners = new Set<(l: Toast[]) => void>();
let lastErrAt = 0;

const emit = () => { const snap = list.slice(); listeners.forEach((f) => f(snap)); };
const busy = (text: string) => /(…|\.\.\.)\s*$/.test(text);

/** How long a toast stays when nobody holds it. */
export function lifetime(text: string, tone: ToastTone): number {
  if (busy(text) && tone !== 'err') return 60000;
  return tone === 'err' ? 12000 : tone === 'warn' ? 9000 : tone === 'ok' ? 5000 : 6000;
}

/** Show a toast. Returns its id. An empty text shows nothing (and from a source, clears that source's work-in-progress line). */
export function toast(text: string, tone: ToastTone = '', opts: { source?: string } = {}): number {
  const t = String(text || '').trim();
  const src = opts.source;
  if (!t) {
    if (src) { const before = list.length; list = list.filter((x) => !(x.source === src && busy(x.text))); if (list.length !== before) emit(); }
    return 0;
  }
  if (tone === 'err') lastErrAt = Date.now();
  const item: Toast = { id: ++seq, text: t, tone, source: src, ms: lifetime(t, tone), at: Date.now() };
  const i = src ? list.findIndex((x) => x.source === src) : -1;
  if (i >= 0) list = [...list.slice(0, i), item, ...list.slice(i + 1)];
  else list = [...list, item].slice(-MAX);
  emit();
  return item.id;
}

export function dismiss(id: number): void {
  const before = list.length;
  list = list.filter((x) => x.id !== id);
  if (list.length !== before) emit();
}

export function clearToasts(): void { if (list.length) { list = []; emit(); } }

export function subscribe(fn: (l: Toast[]) => void): () => void {
  listeners.add(fn); fn(list.slice());
  return () => { listeners.delete(fn); };
}

/**
 * A field's error, raised as a toast unless an error toast went up a moment ago — a refused «Lagre» already said
 * «Sjekk feltene» (or the server's words) once, and five red fields are not five toasts.
 */
export function fieldErrorToast(text: string): void {
  if (!text || Date.now() - lastErrAt < 1500) return;
  toast(text, 'err', { source: 'field-error' });
}

/** For tests. */
export function _reset(): void { list = []; seq = 0; lastErrAt = 0; emit(); }
